import React, { useEffect, useState, useRef } from "react";
import { api, dateTimeFormat } from "../lib/api";
import { Bell, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const ref = useRef(null);

  const unread = items.filter((i) => !i.gelesen).length;

  const load = async () => {
    try {
      const r = await api.get("/me/notifications");
      setItems(r.data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Listen for SW push-click messages
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === "push-click") {
        load();
        if (event.data.data?.url) navigate(event.data.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);

  const markRead = async (id) => {
    await api.post(`/me/notifications/${id}/read`);
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, gelesen: true } : it)));
  };

  const markAllRead = async () => {
    const unreadItems = items.filter((i) => !i.gelesen);
    await Promise.all(unreadItems.map((i) => api.post(`/me/notifications/${i.id}/read`)));
    setItems((prev) => prev.map((it) => ({ ...it, gelesen: true })));
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-full hover:bg-falke-blue/10" data-testid="notification-bell">
        <Bell size={20} className="text-falke-blue" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-falke-orange text-white text-[10px] rounded-full flex items-center justify-center font-bold" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-[0_12px_40px_rgba(45,92,126,0.18)] border border-falke-blue/10 z-50 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-falke-blue/10">
            <h4 className="font-heading font-semibold text-falke-blue">Benachrichtigungen</h4>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-falke-orange font-semibold hover:underline" data-testid="mark-all-read">
                Alle gelesen
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="text-sm text-falke-anthracite/60 py-8 text-center">Keine Benachrichtigungen.</div>
            ) : (
              items.slice(0, 30).map((n) => (
                <div key={n.id} className={`px-4 py-3 border-b border-falke-blue/5 cursor-pointer hover:bg-falke-warmwhite ${!n.gelesen ? "bg-falke-orange/5" : ""}`} onClick={() => markRead(n.id)} data-testid={`notif-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-falke-blue">{n.title}</div>
                      <div className="text-xs text-falke-anthracite/70 mt-0.5">{n.body}</div>
                      <div className="text-[10px] text-falke-anthracite/50 mt-1">{dateTimeFormat(n.erstellt_am)}</div>
                    </div>
                    {!n.gelesen && <span className="w-2 h-2 rounded-full bg-falke-orange flex-shrink-0 mt-1.5" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
